import React, { useState } from 'react';
import axios from 'axios';
import { getInternshipProcessesOfUniversitySupervisor } from '../APIcalls/APICallsForGradingByUniversitySupervisor';
import {
  Tabs,
  Space,
  Form,
  Button,
  Divider,
  message,
  Input,
  Badge,
  Grid,
  Flex,
  Typography,
} from 'antd';
import WorkReportTable from './WorkReport/WorkReportTable';
import {
  validateFloatNumber,
  validateNumberInRange,
} from '../Validators/ValidatorNumber';
import type { InternshipProcessWithNames } from '../Types/interfaces';
import { Roles } from '../Types/interfaces';
import { GradeEvaluationReport } from '../helper/apiHelper';
import { useTranslation } from 'react-i18next';
import ConventionSignComponent from './ConventionSignComponent';
import { EvaluationReportDisplay } from './EvaluationForm/Display/EvaluationReportDisplayComponent';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../Constants/queryKeys';
import { useCurrentUser } from '../hooks/useCurrentUser';
import {
  useEvaluatedReports,
  useReadyToSignConventions,
} from '../hooks/BadgeHooks/SupervisorHooks';
import { NoData } from './NoData';
import { StudyYearDropdown } from './StudyYearDropdown';
import { useCurrentStudyYear } from '../hooks/useStudyYear';

const { useBreakpoint } = Grid;

// use this to prevent infinity rendering because empty array [] reference value always something else
const EMPTY_ARRAY: InternshipProcessWithNames[] = [];

interface GradeEvalFormValues {
  processId: number;
  gradenumber: number;
}

const GradingComponent = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  // graded reports: undefined until the supervisor selects a study year, then the dropdown shows the current one
  const [selectedStudyYearId, setSelectedStudyYearId] = useState<number>();
  const { data: currentStudyYear } = useCurrentStudyYear();
  // the dropdown shows the current study year until another one is selected, the same is listed here
  const shownStudyYearId = selectedStudyYearId ?? currentStudyYear?.id;

  const { data: internshipProcesses = EMPTY_ARRAY, refetch: reFetchData } =
    useQuery({
      queryKey: [
        ...QUERY_KEYS.INTERNSHIP_PROCESSES_OF_SUPERVISOR_AXIOS,
        loggedInUser?.id,
      ],
      queryFn: async () => {
        const internshipProcessesResp = await axios.get(
          getInternshipProcessesOfUniversitySupervisor(loggedInUser?.id ?? 0)
        );
        const internshipProcesses: InternshipProcessWithNames[] =
          internshipProcessesResp.data;
        return internshipProcesses;
      },
      enabled: !!loggedInUser?.id,
      meta: {
        onError: () => {
          messageApi.error(t('gradingComp.failedToLoadInternshipProcesses'));
        },
      },
    });

  const readyToSignConventions = useReadyToSignConventions(internshipProcesses);

  const activeReports: InternshipProcessWithNames[] =
    internshipProcesses.filter((process) =>
      process.processSteps.some(
        (step) => step.stepName === 'WorkReportFilling' && step.stepStatus !== 3
      )
    );

  const notAcceptedReports: InternshipProcessWithNames[] =
    internshipProcesses.filter(
      (process) =>
        process.processSteps.some(
          (step) =>
            step.stepName === 'WorkReportFilling' && step.stepStatus === 3
        ) &&
        process.processSteps.some(
          (step) =>
            step.stepName === 'ValidateWorkReportByTutor' &&
            step.stepStatus !== 3
        )
    );

  const acceptedReports: InternshipProcessWithNames[] =
    internshipProcesses.filter(
      (process) =>
        process.processSteps.some(
          (step) =>
            step.stepName === 'ValidateWorkReportByTutor' &&
            step.stepStatus === 3
        ) &&
        process.processSteps.some(
          (step) =>
            step.stepName === 'CompletingEvaluationReportByOrganization' &&
            step.stepStatus !== 3
        )
    );

  const evaluatedReports = useEvaluatedReports(internshipProcesses);

  const gradedReports: InternshipProcessWithNames[] =
    internshipProcesses.filter(
      (process) =>
        process.processSteps.some(
          (step) =>
            step.stepName === 'GradingWorkReportByUniversitySupervisor' &&
            step.stepStatus === 3
        ) && process.studyYearId === shownStudyYearId
    );

  const gradeEvalReport = async (
    evalReport: GradeEvalFormValues
  ): Promise<void> => {
    try {
      const resp = await GradeEvaluationReport(evalReport.processId, {
        finalGrade: evalReport.gradenumber,
      });
      if (resp === undefined) {
        messageApi.error(t('gradingComp.cannotSign'));
        return;
      }
      await reFetchData();

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_OF_SUPERVISOR_AXIOS,
      });

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_OF_SUPERVISOR,
      });

      messageApi.success(t('gradingComp.gradeSuccess'));
    } catch (error) {
      messageApi.error(t('gradeError'));
      console.error(error);
    }
  };

  return (
    <>
      {contextHolder}
      <Tabs
        type="card"
        tabPosition="top"
        centered
        style={{ marginBottom: 100 }}
        items={[
          {
            key: '1',
            label: (
              <span>
                {t('gradingComp.conventions')}{' '}
                <Badge
                  count={readyToSignConventions.length}
                  overflowCount={10}
                  size="small"
                />
              </span>
            ),
            children: (
              <ConventionSignComponent
                menuGroup="Supervisor"
                filterByStudyYear={true}
                enableDownload={true}
                role={Roles.CadruDidactic}
                recount={async () => {
                  queryClient.invalidateQueries({
                    queryKey:
                      QUERY_KEYS.INTERNSHIP_PROCESSES_OF_SUPERVISOR_AXIOS,
                  });
                  queryClient.invalidateQueries({
                    queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_OF_SUPERVISOR,
                  });
                }}
              />
            ),
          },
          {
            key: '2',
            label: <span>{t('gradingComp.ongoingReports')} </span>,
            children:
              activeReports && activeReports.length > 0 ? (
                <Tabs
                  tabPosition={screens.md ? 'left' : 'top'}
                  items={activeReports.map(
                    (process: InternshipProcessWithNames) => {
                      return {
                        key: String(process.id),
                        label: process.studentName,
                        children: (
                          <>
                            <Divider plain style={{ fontSize: 20 }}>
                              {' '}
                              {t('gradingComp.worklogs')}{' '}
                            </Divider>
                            <WorkReportTable
                              WorkLogId={process.workLogId}
                              WorkLogDocumentUrl={process.workLogDocumentUrl}
                              WorkLogDocumentStatus={
                                process.currentOrganizationStepName
                              }
                              role={Roles.CadruDidactic}
                              signable={false}
                              onSubmit={false}
                            />
                          </>
                        ),
                      };
                    }
                  )}
                />
              ) : (
                <NoData />
              ),
          },
          {
            key: '3',
            label: (
              <span>
                {t('gradingComp.waitingReports')}{' '}
                <Badge
                  count={notAcceptedReports.length}
                  overflowCount={10}
                  size="small"
                />
              </span>
            ),
            children:
              notAcceptedReports && notAcceptedReports.length > 0 ? (
                <Tabs
                  tabPosition={screens.md ? 'left' : 'top'}
                  items={notAcceptedReports.map(
                    (process: InternshipProcessWithNames) => {
                      return {
                        key: String(process.id),
                        label: process.studentName,
                        children: (
                          <>
                            <Divider plain style={{ fontSize: 20 }}>
                              {' '}
                              {t('gradingComp.worklogs')}{' '}
                            </Divider>
                            <WorkReportTable
                              WorkLogId={process.workLogId}
                              WorkLogDocumentUrl={process.workLogDocumentUrl}
                              WorkLogDocumentStatus={
                                process.currentOrganizationStepName
                              }
                              role={Roles.CadruDidactic}
                              signable={false}
                              onSubmit={false}
                            />
                          </>
                        ),
                      };
                    }
                  )}
                />
              ) : (
                <NoData />
              ),
          },
          {
            key: '4',
            label: <span>{t('gradingComp.acceptedByTutor')} </span>,
            children:
              acceptedReports && acceptedReports.length > 0 ? (
                <Tabs
                  tabPosition={screens.md ? 'left' : 'top'}
                  items={acceptedReports.map(
                    (process: InternshipProcessWithNames) => {
                      return {
                        key: String(process.id),
                        label: process.studentName,
                        children: (
                          <>
                            <Divider plain style={{ fontSize: 20 }}>
                              {' '}
                              {t('gradingComp.worklogs')}{' '}
                            </Divider>
                            <WorkReportTable
                              WorkLogId={process.workLogId}
                              WorkLogDocumentUrl={process.workLogDocumentUrl}
                              WorkLogDocumentStatus={
                                process.currentOrganizationStepName
                              }
                              role={Roles.CadruDidactic}
                              signable={false}
                              onSubmit={false}
                            />
                          </>
                        ),
                      };
                    }
                  )}
                />
              ) : (
                <NoData />
              ),
          },
          {
            key: '5',
            label: (
              <span>
                {t('gradingComp.evaluatedReports')}{' '}
                <Badge
                  count={evaluatedReports.length}
                  overflowCount={10}
                  size="small"
                />
              </span>
            ),
            children:
              evaluatedReports && evaluatedReports.length > 0 ? (
                <Tabs
                  tabPosition={screens.md ? 'left' : 'top'}
                  items={evaluatedReports.map(
                    (process: InternshipProcessWithNames) => {
                      return {
                        key: String(process.id),
                        label: process.studentName,
                        children: (
                          <>
                            <Divider plain style={{ fontSize: 20 }}>
                              {' '}
                              {t('gradingComp.worklogs')}{' '}
                            </Divider>
                            <WorkReportTable
                              WorkLogId={process.workLogId}
                              WorkLogDocumentUrl={process.workLogDocumentUrl}
                              WorkLogDocumentStatus={
                                process.currentOrganizationStepName
                              }
                              role={Roles.CadruDidactic}
                              signable={false}
                              onSubmit={false}
                            />
                            <Divider plain style={{ fontSize: 20 }}>
                              {' '}
                              {t('gradingComp.evalReport')}{' '}
                            </Divider>
                            <EvaluationReportDisplay
                              organizationId={process.organizationId}
                              reportId={process.evaluationReportId}
                              studentName={process.studentName
                                .split(' ')
                                .join('')}
                              isSignable={false}
                            />

                            <Divider>
                              <Space>{t('gradingComp.grade')}</Space>
                            </Divider>

                            <Form
                              key={process.id}
                              layout="inline"
                              size="large"
                              onFinish={gradeEvalReport}
                              style={{
                                padding: 10,
                                borderRadius: 10,
                                marginTop: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Form.Item
                                name="gradenumber"
                                style={{ marginBottom: 0, height: 40 }}
                                rules={[
                                  {
                                    required: true,
                                    message: t('validator.required'),
                                  },
                                  {
                                    validator: validateFloatNumber,
                                    message: t('validator.validateFloatNumber'),
                                  },
                                  {
                                    validator: validateNumberInRange(1, 10),
                                  },
                                ]}
                              >
                                <Input />
                              </Form.Item>

                              <Form.Item
                                name="processId"
                                hidden
                                initialValue={process.id}
                              >
                                <Input type="hidden" disabled={true} />
                              </Form.Item>

                              <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                  htmlType="submit"
                                  block
                                  type="primary"
                                  style={{ height: 40 }}
                                >
                                  {t('gradingComp.submit')}
                                </Button>
                              </Form.Item>
                            </Form>
                          </>
                        ),
                      };
                    }
                  )}
                />
              ) : (
                <NoData />
              ),
          },
          {
            key: '6',
            label: <span>{t('gradingComp.gradedReports')} </span>,
            children: (
              <>
                <Flex
                  align="center"
                  justify="start"
                  gap={8}
                  wrap
                  style={{ marginBottom: 10 }}
                >
                  <Typography.Text strong>
                    {t('studyYear.label')}:
                  </Typography.Text>
                  <StudyYearDropdown
                    value={selectedStudyYearId}
                    onChange={setSelectedStudyYearId}
                  />
                </Flex>
                {gradedReports && gradedReports.length > 0 ? (
                  <Tabs
                    // a new tab list (first student selected) for every study year
                    key={shownStudyYearId}
                    tabPosition={screens.md ? 'left' : 'top'}
                    items={gradedReports.map(
                      (process: InternshipProcessWithNames) => {
                        return {
                          key: String(process.id),
                          label: process.studentName,
                          children: (
                            <>
                              <Divider plain style={{ fontSize: 20 }}>
                                {' '}
                                {t('gradingComp.worklogs')}{' '}
                              </Divider>
                              <WorkReportTable
                                WorkLogId={process.workLogId}
                                WorkLogDocumentUrl={process.workLogDocumentUrl}
                                WorkLogDocumentStatus={
                                  process.currentOrganizationStepName
                                }
                                role={Roles.CadruDidactic}
                                signable={false}
                                onSubmit={false}
                              />
                              <Divider plain style={{ fontSize: 20 }}>
                                {' '}
                                {t('gradingComp.evalReport')}{' '}
                              </Divider>
                              <EvaluationReportDisplay
                                isSignable={false}
                                organizationId={process.organizationId}
                                reportId={process.evaluationReportId}
                                studentName={process.studentName
                                  .split(' ')
                                  .join('')}
                              />
                            </>
                          ),
                        };
                      }
                    )}
                  />
                ) : (
                  <NoData />
                )}
              </>
            ),
          },
        ]}
      />
    </>
  );
};

export default GradingComponent;
